import React from "react";

const formatInline = (text: string): React.ReactNode[] => {
  const parts: React.ReactNode[] = [];
  const pattern = /(\*\*[^*]+\*\*|(?<!\*)\*(?!\*)[^*]+(?<!\*)\*(?!\*))/g;
  let cursor = 0;
  for (const match of text.matchAll(pattern)) {
    const index = match.index ?? 0;
    if (index > cursor) parts.push(text.slice(cursor, index));
    const token = match[0];
    if (token.startsWith("**")) {
      parts.push(<strong key={`${index}-strong`}>{token.slice(2, -2)}</strong>);
    } else {
      parts.push(<em key={`${index}-em`}>{token.slice(1, -1)}</em>);
    }
    cursor = index + token.length;
  }
  if (cursor < text.length) parts.push(text.slice(cursor));
  return parts;
};

const renderBlock = (block: string, i: number): React.ReactNode => {
  const trimmed = block.trim();
  if (!trimmed) return null;

  // Heading 2
  if (trimmed.startsWith("## ") && !trimmed.startsWith("### ") && !trimmed.includes("\n")) {
    return <h2 key={i}>{formatInline(trimmed.slice(3))}</h2>;
  }
  // Heading 3
  if (trimmed.startsWith("### ") && !trimmed.includes("\n")) {
    return <h3 key={i}>{formatInline(trimmed.slice(4))}</h3>;
  }
  // Blockquote
  if (trimmed.startsWith("> ") && !trimmed.includes("\n")) {
    return (
      <blockquote key={i}>
        <p>{formatInline(trimmed.slice(2))}</p>
      </blockquote>
    );
  }
  // Table
  if (trimmed.startsWith("| ")) {
    const rows = trimmed
      .split("\n")
      .filter((r) => !/^\|[\s-]+\|/.test(r) && !r.startsWith("|--") && !r.startsWith("| --"));
    const headers = rows[0]?.split("|").filter(Boolean).map((h) => h.trim());
    const dataRows = rows.slice(1).map((r) =>
      r.split("|").filter(Boolean).map((c) => c.trim())
    );
    return (
      <table key={i}>
        <thead>
          <tr>{headers?.map((h, j) => <th key={j}>{h}</th>)}</tr>
        </thead>
        <tbody>
          {dataRows.map((row, j) => (
            <tr key={j}>
              {row.map((cell, k) => (
                <td key={k}>{formatInline(cell)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    );
  }
  // Unordered list
  if (trimmed.startsWith("- ")) {
    const items = trimmed.split("\n").filter((l) => l.trimStart().startsWith("- "));
    return (
      <ul key={i}>
        {items.map((item, j) => (
          <li key={j}>{formatInline(item.replace(/^\s*- /, ""))}</li>
        ))}
      </ul>
    );
  }
  // Ordered list
  if (/^\d+\. /.test(trimmed)) {
    const items = trimmed.split("\n").filter((l) => /^\s*\d+\. /.test(l));
    return (
      <ol key={i}>
        {items.map((item, j) => (
          <li key={j}>{formatInline(item.replace(/^\s*\d+\. /, ""))}</li>
        ))}
      </ol>
    );
  }
  // Paragraph
  return <p key={i}>{formatInline(trimmed)}</p>;
};

/** Split a raw content block into atomic sub-blocks by detecting
 *  headings, list starts, and blockquotes on individual lines. */
const splitIntoBlocks = (content: string): string[] => {
  const rawBlocks = content.split("\n\n");
  const result: string[] = [];

  for (const raw of rawBlocks) {
    const lines = raw.split("\n");
    let current: string[] = [];

    const flush = () => {
      if (current.length > 0) {
        result.push(current.join("\n"));
        current = [];
      }
    };

    for (const line of lines) {
      const isHeading = /^#{2,3} /.test(line);
      const isList = /^\s*- /.test(line) || /^\s*\d+\. /.test(line);
      const isBlockquote = line.startsWith("> ");
      const isTable = line.startsWith("| ");

      if (isHeading) {
        flush();
        result.push(line);
      } else if (
        current.length > 0 &&
        isList &&
        !current[current.length - 1]?.trimStart().startsWith("- ") &&
        !/^\s*\d+\. /.test(current[current.length - 1] || "")
      ) {
        // Transition from non-list to list
        flush();
        current.push(line);
      } else if (
        current.length > 0 &&
        isTable &&
        !current[current.length - 1]?.startsWith("| ")
      ) {
        flush();
        current.push(line);
      } else if (isBlockquote && current.length > 0 && !current[0]?.startsWith("> ")) {
        flush();
        current.push(line);
      } else {
        current.push(line);
      }
    }
    flush();
  }

  return result;
};

interface ArticleContentProps {
  content: string;
}

const ArticleContent = ({ content }: ArticleContentProps) => {
  const blocks = splitIntoBlocks(content);
  return <>{blocks.map((block, i) => renderBlock(block, i))}</>;
};

export default ArticleContent;
